import { Component, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { RackeoLocaleService } from '../shared/services/locale.service';
import type {
  RackeoComparisonPriceRow,
  RackeoPlanContractNormalized,
  RackeoWebHostingPymeData,
  RackeoWebHostingPlan,
} from '../shared/services/locale.service';
import { MetaService } from '@ngx-meta/core';
import { CanonicalService } from '../shared/services/canonical.service';
import { RACKEO_SITE } from '../shared/rackeo-site';
import { RackeoCartService } from '../shared/services/cart.service';
import { prepareWebHostingPlansData } from '../shared/web-hosting-pricing.util';

export interface PymePlanFeature {
  label: string;
  value: string;
}

export type PymePlanContract = RackeoPlanContractNormalized;

export type PymePlan = Omit<RackeoWebHostingPlan, 'contracts'> & {
  contracts: RackeoPlanContractNormalized[];
};

export interface PymeComparisonRow {
  section?: string;
  feature: string;
  helpText?: string;
  p1: string;
  p2: string;
  p3: string;
}

const PYME_HERO_DEFAULT_SRC = 'assets/images/rackeo/web-hosting/pyme/banner-web-hosting-pyme-chile.png';
const PYME_HERO_DEFAULT_ALT =
  'Data center propio: enlace fibra óptica, grupo electrógeno de respaldo, UPS para servidores';

@Component({
  selector: 'app-web-hosting-pyme',
  templateUrl: './web-hosting-pyme.component.html',
  styleUrls: ['./web-hosting-pyme.component.scss']
})
export class WebHostingPymeComponent implements OnInit {
  activeTab = 0;
  readonly pageTabsTitle = 'Planes Web Hosting Pyme';
  readonly tabPills = ['Generales', 'Características', 'Comparar planes'];
  readonly tabSectionTitles = ['GENERALES', 'CARACTERÍSTICAS', 'COMPARAR PLANES'];
  pymeHeroSrc = PYME_HERO_DEFAULT_SRC;
  pymeHeroAlt = PYME_HERO_DEFAULT_ALT;
  plans: PymePlan[] = [];
  comparisonPrices: RackeoComparisonPriceRow[] = [];
  comparisonRows: PymeComparisonRow[] = [];

  private readonly comparisonData: PymeComparisonRow[] = [
    { section: 'ESPACIO WEB', feature: 'Espacio en el Disco', helpText: 'Espacio disponible para almacenar archivos web', p1: 'ILIMITADO', p2: 'ILIMITADO', p3: 'ILIMITADO' },
    { feature: 'Transferencia Mensual', helpText: 'Cantidad de información transferida desde o hacia el sitio o servidor', p1: 'ILIMITADA', p2: 'ILIMITADA', p3: 'ILIMITADA' },
    { feature: 'Inodos', helpText: 'Índices en la estructura de archivos del disco', p1: 'ILIMITADOS', p2: 'ILIMITADOS', p3: 'ILIMITADOS' },
    { feature: 'Respaldos', helpText: 'Copia de seguridad de la información del servidor', p1: 'ILIMITADOS', p2: 'ILIMITADOS', p3: 'ILIMITADOS' },
    { feature: 'cPanel', helpText: 'Panel de control para administrar tu sitio', p1: 'check', p2: 'check', p3: 'check' },
    { feature: 'Instalador Softaculous', helpText: 'Instala aplicaciones con un clic', p1: 'check', p2: 'check', p3: 'check' },
    { section: 'EXTRAS', feature: 'Site Publisher', helpText: 'Crea tu sitio en minutos sin conocimientos avanzados', p1: 'check', p2: 'check', p3: 'check' },
    { section: 'SERVICIOS E-MAIL', feature: 'Cuentas de e-mail', helpText: 'Cuentas de correo configurables', p1: '5', p2: '20', p3: 'ILIMITADAS' },
    { feature: 'SMTP, IMAP y POP3', helpText: 'Tipos de conexión', p1: 'check', p2: 'check', p3: 'check' },
    { feature: 'Webmail', helpText: 'Gestiona tu correo desde el navegador', p1: 'check', p2: 'check', p3: 'check' },
    { feature: 'AntiSpam', helpText: 'Protección contra correo no deseado', p1: 'check', p2: 'check', p3: 'check' },
    { section: 'SERVICIOS GENERALES', feature: 'Dominio Permitidos', helpText: 'Dominio principal de su sitio web', p1: '1', p2: '2', p3: 'ILIMITADOS' },
    { feature: 'Sub Dominios', helpText: 'Subdominios dentro de tu dominio principal', p1: '5', p2: '20', p3: 'ILIMITADOS' },
    { feature: 'Redireccionados', helpText: 'Páginas de redirección', p1: 'check', p2: 'check', p3: 'check' },
    { feature: 'Cuentas FTP', helpText: 'Protocolo de transferencia de archivos', p1: 'check', p2: 'check', p3: 'check' },
    { section: 'BASES DE DATOS', feature: 'Base de Datos', helpText: 'Conjunto de datos almacenados sistemáticamente', p1: 'ILIMITADAS', p2: 'ILIMITADAS', p3: 'ILIMITADAS' },
    { feature: 'MySQL, PostgreSQL', helpText: 'Sistemas de gestión de base de datos', p1: 'check', p2: 'check', p3: 'check' },
    { section: 'PROGRAMACIÓN', feature: 'PHP 5 y Posterior', helpText: 'Lenguaje para páginas dinámicas', p1: 'check', p2: 'check', p3: 'check' },
    { feature: 'Cron Job', helpText: 'Administrador de tareas programadas', p1: 'check', p2: 'check', p3: 'check' },
    { feature: 'Alta en Buscadores', helpText: 'Visualiza tu sitio en buscadores', p1: 'check', p2: 'check', p3: 'check' },
    { section: 'SEGURIDAD', feature: 'Respaldos Diario, Semanal y Mensual', helpText: 'Respaldamos tu sitio diariamente y lo guardamos por una semana', p1: 'check', p2: 'check', p3: 'check' },
    { feature: 'Antivirus', helpText: 'Servidores protegidos con antivirus', p1: 'check', p2: 'check', p3: 'check' },
    { feature: 'Firewall', helpText: 'Protección de red y acceso no autorizado', p1: 'check', p2: 'check', p3: 'check' },
    { section: 'SOPORTE', feature: 'Soporte Básico Preferencial (Ticket e-mail)', helpText: 'Soporte profesional ingresando un ticket desde su área panel', p1: 'INCLUIDO', p2: 'INCLUIDO', p3: 'INCLUIDO' }
  ];

  constructor(
    private readonly locale: RackeoLocaleService,
    public readonly cart: RackeoCartService,
    private readonly meta: MetaService,
    private readonly canonical: CanonicalService
  ) {}

  ngOnInit(): void {
    this.comparisonRows = this.comparisonData;
    const country = RACKEO_SITE.countryName;
    this.meta.setTitle(`Web Hosting Pyme ${country} | Respaldos Diarios, cPanel | Rackeo`);
    this.meta.setTag('description', `Planes Web Hosting Pyme con cPanel, respaldos diarios, antivirus y firewall. Ideal para pequeñas y medianas empresas en ${country}.`);
    this.meta.setTag('og:type', 'website');
    this.meta.setTag('og:image:type', 'image/png');
    this.meta.setTag('og:image', RACKEO_SITE.ogImageUrl);
    this.meta.setTag('og:image:width', '1200');
    this.meta.setTag('og:image:height', '1200');
    this.meta.setTag('og:image:alt', `Web Hosting Pyme ${country} | Respaldos Diarios, cPanel | Rackeo`);
    this.meta.setTag('og:locale', RACKEO_SITE.ogLocale);
    this.canonical.set('/web-hosting-pyme');
    this.locale.getContent().pipe(take(1)).subscribe((content) => {
      if (content?.webHostingPyme) {
        this.applyPymeFile(content.webHostingPyme);
      }
    });
  }

  private applyPymeFile(file: RackeoWebHostingPymeData): void {
    this.pymeHeroSrc = file.heroImage?.trim() ? file.heroImage.trim() : PYME_HERO_DEFAULT_SRC;
    this.pymeHeroAlt = file.heroAlt?.trim() ? file.heroAlt.trim() : PYME_HERO_DEFAULT_ALT;

    const priceLocale = this.locale.getContentSnapshot()?.cart?.priceLocale ?? 'es-CL';
    const { plans, comparisonPrices } = prepareWebHostingPlansData(
      file.plans || [],
      priceLocale
    );

    this.plans = plans;
    this.comparisonPrices = comparisonPrices;
  }
  setTab(index: number): void {
    this.activeTab = index;
  }

  get site() {
    return RACKEO_SITE;
  }
}
